import * as React from 'react';
import {
  Alert, Box, Button, Container, Divider, Grid, Paper, Stack, TextField, Typography,
} from '@mui/material';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import { api } from '../lib/api';
import ordinateurImage from '../assets/images/ordinateur.jpg';
import tabletteImage from '../assets/images/tablet.jpg';
import telephoneImage from '../assets/images/telephone.jpg';
import terreImage from '../assets/images/terre.png';
import collectLogo from '../assets/logo/collect.png';

const GREEN = '#197348';
const GREEN_DARK = '#123a2a';
const GREEN_LIGHT = '#edf7f0';
const BORDER = '#dce9e0';
const emptyForm = { companyName: '', contactName: '', email: '', city: '' };
const FORM_FIELD_SX = {
  '& label.Mui-focused': { color: GREEN },
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: BORDER },
    '&:hover fieldset': { borderColor: GREEN },
    '&.Mui-focused fieldset': { borderColor: GREEN },
  },
};

export default function CollectLanding() {
  const [form, setForm] = React.useState(emptyForm);
  const [sending, setSending] = React.useState(false);
  const [feedback, setFeedback] = React.useState(null);
  const change = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));

  const submit = async (event) => {
    event.preventDefault();
    setSending(true);
    setFeedback(null);
    try {
      await api.post('/collect/public/inquiries', form);
      setForm(emptyForm);
      setFeedback({ type: 'success', text: 'Merci, votre demande a bien été envoyée. Nous allons vous recontacter.' });
    } catch (error) {
      setFeedback({ type: 'error', text: error?.response?.data?.message || 'La demande n’a pas pu être envoyée.' });
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fff', color: GREEN_DARK }}>
      <Box component="header" sx={{ bgcolor: '#fff', borderBottom: `1px solid ${BORDER}` }}>
        <Container maxWidth="lg">
          <Stack direction="row" alignItems="center" sx={{ minHeight: 68 }}>
            <Box component="a" href="/" aria-label="123back Collect" sx={{ display: 'inline-flex', alignItems: 'center' }}>
              <Box component="img" src={collectLogo} alt="123back Collect" sx={{ display: 'block', width: { xs: 190, sm: 220 }, height: 'auto' }} />
            </Box>
            <Button href="#contact" variant="contained" sx={{ ml: 'auto', bgcolor: GREEN, textTransform: 'none', boxShadow: 'none', fontWeight: 800 }}>
              Organiser une collecte
            </Button>
          </Stack>
        </Container>
      </Box>

      <Box sx={{ bgcolor: GREEN, color: '#fff', py: { xs: 5, md: 8 } }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography component="h1" sx={{ fontSize: { xs: 36, md: 52 }, lineHeight: 1.1, fontWeight: 900 }}>
            Organisez une collecte d’appareils dans votre entreprise
          </Typography>
          <Typography sx={{ mt: 2.5, fontSize: { xs: 17, md: 19 }, lineHeight: 1.65, opacity: 0.95 }}>
            Nous installons un point de collecte dans vos locaux pour récupérer les téléphones, tablettes et ordinateurs portables cassés ou inutilisés de vos collaborateurs.
          </Typography>
          <Typography sx={{ mt: 1.5, fontSize: 17, lineHeight: 1.65, opacity: 0.9 }}>
            Une action écologique simple à mettre en place, avec un suivi transparent jusqu’au bilan final de la collecte.
          </Typography>
          <Button href="#contact" size="large" variant="contained" sx={{ mt: 3, bgcolor: '#fff', color: GREEN, textTransform: 'none', fontWeight: 900, boxShadow: 'none', '&:hover': { bgcolor: '#f4f8f5' } }}>
            Nous contacter
          </Button>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 5, md: 7 } }}>
        <Container maxWidth="lg">
          <Typography variant="h3" textAlign="center" sx={{ fontSize: { xs: 29, md: 38 }, fontWeight: 900 }}>
            Donnez une seconde vie aux appareils oubliés
          </Typography>
          <Typography textAlign="center" sx={{ color: '#5c7066', maxWidth: 790, mx: 'auto', mt: 2, fontSize: 17, lineHeight: 1.7 }}>
            La collecte peut être organisée sur une semaine ou sur une période adaptée à votre entreprise. La caisse est placée dans un espace accessible et vos collaborateurs y déposent simplement les appareils qu’ils n’utilisent plus.
          </Typography>
          <Box sx={{ mt: 4, maxWidth: 1020, mx: 'auto', display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 3 }}>
            {[
              { label: 'Téléphones', image: telephoneImage },
              { label: 'Tablettes', image: tabletteImage },
              { label: 'Ordinateurs portables', image: ordinateurImage },
            ].map((item) => (
              <Box key={item.label} sx={{ width: '100%', minWidth: 0 }}>
                <Box sx={{ width: '100%', height: { xs: 230, md: 220 }, overflow: 'hidden', borderRadius: 3 }}>
                  <Box component="img" src={item.image} alt={item.label} sx={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center center' }} />
                </Box>
                  <Typography textAlign="center" fontWeight={900} fontSize={18} sx={{ mt: 1.5 }}>{item.label}</Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      <Box sx={{ bgcolor: GREEN_DARK, color: '#fff', pt: { xs: 5, md: 7 }, overflow: 'hidden' }}>
        <Container maxWidth="lg">
          <Box sx={{ maxWidth: 960, mx: 'auto', textAlign: 'center' }}>
              <Typography variant="h3" sx={{ fontSize: { xs: 29, md: 38 }, fontWeight: 900 }}>
                Un impact écologique concret et mesurable
              </Typography>
              <Typography sx={{ mt: 2, color: '#d1e3d8', fontSize: 17, lineHeight: 1.75 }}>
                De nombreux appareils électroniques restent inutilisés dans les tiroirs alors qu’ils contiennent des composants qui peuvent encore être valorisés. Les collecter permet de limiter les déchets électroniques, d’orienter les éléments dangereux vers les bonnes filières et de favoriser le réemploi des pièces encore exploitables.
              </Typography>
              <Typography sx={{ mt: 1.5, color: '#d1e3d8', fontSize: 17, lineHeight: 1.75 }}>
                Chaque collecte est suivie du début à la fin. Votre entreprise sait combien d’appareils ont été récupérés, combien de pièces ont été dépolluées ou recyclées et combien ont pu être conservées pour le réemploi. Les résultats sont présentés clairement, sans estimation vague, dans un rapport accessible à vos équipes.
              </Typography>
              <Grid container spacing={2} sx={{ mt: 2.5 }}>
                {['Moins de déchets électroniques', 'Traçabilité de la collecte', 'Résultats partagés avec vos équipes'].map((item) => (
                  <Grid item xs={12} md={4} key={item} sx={{ display: 'flex' }}>
                    <Paper elevation={0} sx={{ width: '100%', minHeight: 76, px: 2, py: 1.5, boxSizing: 'border-box', bgcolor: 'rgba(255,255,255,.09)', border: '1px solid rgba(255,255,255,.16)', borderRadius: 2.5, display: 'grid', placeItems: 'center' }}>
                      <Typography sx={{ color: '#b7ebc9', fontWeight: 900 }}>{item}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
          </Box>
        </Container>
        <Box sx={{ position: 'relative', height: { xs: 125, md: 185 }, overflow: 'hidden', mt: { xs: 0, md: -1 } }}>
          <Box component="img" src={terreImage} alt="La Terre vue depuis l’espace" sx={{ position: 'absolute', left: '50%', bottom: -2, transform: 'translateX(-50%)', display: 'block', width: { xs: '115%', md: '78%' }, maxWidth: 980, height: 'auto' }} />
        </Box>
      </Box>

      <Box id="fonctionnement" sx={{ bgcolor: GREEN_LIGHT, py: { xs: 5, md: 7 } }}>
        <Container maxWidth="lg">
          <Typography variant="h3" sx={{ fontSize: { xs: 29, md: 38 }, fontWeight: 900, mb: 4 }}>
            Comment ça marche ?
          </Typography>
          <Stack spacing={2.5}>
            {[
              [Inventory2OutlinedIcon, '1. Mise en place', 'Nous préparons avec vous les dates, l’emplacement de la caisse et les informations à transmettre aux collaborateurs. Votre page personnalisée permet de suivre la préparation.'],
              [EventAvailableOutlinedIcon, '2. Collecte pendant une semaine', 'La caisse reste dans vos locaux pendant une semaine. Les dates, le lieu et les consignes restent accessibles à tous les collaborateurs.'],
              [LocalShippingOutlinedIcon, '3. Récupération', 'À la fin de la semaine, nous récupérons les appareils puis nous réalisons le tri, la dépollution et le traitement dans les filières adaptées.'],
              [AssessmentOutlinedIcon, '4. Bilan transparent', 'Vous recevez les résultats détaillés de la collecte sur une page que vous pouvez partager avec toutes vos équipes.'],
            ].map(([Icon, title, text]) => (
              <Paper
                key={title}
                elevation={0}
                sx={{
                  p: { xs: 2.5, md: 3.5 },
                  border: `1px solid ${BORDER}`,
                  borderRadius: 3,
                  bgcolor: '#fff',
                }}
              >
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 2, sm: 3 }} alignItems={{ sm: 'center' }}>
                  <Box sx={{ width: 64, height: 64, flexShrink: 0, bgcolor: GREEN_LIGHT, borderRadius: 2.5, display: 'grid', placeItems: 'center' }}>
                    <Icon sx={{ color: GREEN, fontSize: 36 }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight={900}>{title}</Typography>
                    <Typography sx={{ color: '#5c7066', mt: 0.5, lineHeight: 1.65 }}>{text}</Typography>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Container>
      </Box>

      <Box id="bilan" sx={{ py: { xs: 5, md: 8 } }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h3" sx={{ fontSize: { xs: 29, md: 38 }, fontWeight: 900 }}>
            Une interface personnalisée pour votre entreprise
          </Typography>
          <Typography sx={{ color: '#5c7066', mt: 2, mx: 'auto', maxWidth: 760, fontSize: 17, lineHeight: 1.7 }}>
            Votre entreprise dispose de sa propre adresse sur collect.123back.eu. La page assure un suivi clair à chaque étape : elle présente d’abord les dates, le lieu et les consignes, puis affiche le bilan environnemental une fois les appareils traités.
          </Typography>
          <Paper elevation={0} sx={{ mt: 4, mx: 'auto', maxWidth: 680, border: `1px solid ${BORDER}`, borderRadius: 3, overflow: 'hidden', textAlign: 'left', boxShadow: '0 16px 45px rgba(18,58,42,.10)' }}>
            <Box sx={{ px: 2.5, py: 1.4, bgcolor: '#e9efeb', color: '#53675c', fontSize: 13 }}>collect.123back.eu/votre-entreprise</Box>
            <Box sx={{ p: { xs: 2.5, md: 4 } }}>
              <Typography color={GREEN} fontWeight={900}>Collecte de votre entreprise</Typography>
              <Typography color={GREEN_DARK} variant="h5" fontWeight={900} sx={{ mt: 1 }}>Notre collecte est en cours</Typography>
              <Typography color="#5c7066" sx={{ mt: 1 }}>Retrouvez ici les dates, l’emplacement du point de collecte et toutes les informations utiles.</Typography>
              <Box sx={{ height: 8, bgcolor: '#deebe2', borderRadius: 9, mt: 3 }}><Box sx={{ width: '58%', height: '100%', bgcolor: GREEN, borderRadius: 9 }}/></Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 3 }} sx={{ mt: 3 }}><Typography color="#5c7066">✓ Informations</Typography><Typography color="#5c7066">✓ Suivi</Typography><Typography color="#5c7066">✓ Rapport final</Typography></Stack>
            </Box>
          </Paper>
          <Typography sx={{ color: '#5c7066', mt: 2, fontSize: 14 }}>Le rapport reste accessible pendant 30 jours après sa publication et peut être partagé à tous les collaborateurs.</Typography>
        </Container>
      </Box>

      <Box id="contact" sx={{ bgcolor: '#f6f9f7', py: { xs: 5, md: 7 } }}>
        <Container maxWidth="sm">
          <Typography variant="h3" textAlign="center" sx={{ fontSize: { xs: 29, md: 38 }, fontWeight: 900 }}>
            Organiser une collecte
          </Typography>
          <Typography textAlign="center" sx={{ color: '#5c7066', mt: 1.5, mb: 3 }}>
            Laissez-nous simplement vos coordonnées. Nous reviendrons vers vous pour préparer la collecte et demander les informations complémentaires.
          </Typography>
          <Paper component="form" onSubmit={submit} elevation={0} sx={{ p: { xs: 2.5, md: 3.5 }, border: `1px solid ${BORDER}`, borderRadius: 3 }}>
            <Stack spacing={2}>
              <TextField required fullWidth label="Entreprise" value={form.companyName} onChange={change('companyName')} sx={FORM_FIELD_SX} />
              <TextField required fullWidth label="Nom du contact" value={form.contactName} onChange={change('contactName')} sx={FORM_FIELD_SX} />
              <TextField required type="email" fullWidth label="E-mail" value={form.email} onChange={change('email')} sx={FORM_FIELD_SX} />
              <TextField required fullWidth label="Ville" value={form.city} onChange={change('city')} sx={FORM_FIELD_SX} />
              {feedback && <Alert severity={feedback.type}>{feedback.text}</Alert>}
              <Button type="submit" disabled={sending} variant="contained" size="large" sx={{ bgcolor: GREEN, textTransform: 'none', fontWeight: 900, boxShadow: 'none' }}>
                {sending ? 'Envoi…' : 'Envoyer ma demande'}
              </Button>
            </Stack>
          </Paper>
        </Container>
      </Box>

      <Box component="footer" sx={{ bgcolor: GREEN_DARK, color: '#c9ddd1', py: 3 }}>
        <Container maxWidth="lg">
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={2}>
            <Box component="img" src={collectLogo} alt="123back Collect" sx={{ display: 'block', width: 190, height: 'auto' }} />
            <Stack direction="row" spacing={3}>
              <Typography component="a" href="mailto:contact@123back.eu" sx={{ color: 'inherit', textDecoration: 'none' }}>contact@123back.eu</Typography>
              <Typography component="a" href={typeof window !== 'undefined' && window.location.hostname.toLowerCase().startsWith('collect.') ? '/cookies' : '/collect/cookies'} sx={{ color: 'inherit', textDecoration: 'none' }}>Cookies</Typography>
            </Stack>
          </Stack>
          <Divider sx={{ borderColor: 'rgba(255,255,255,.12)', my: 2 }} />
          <Typography variant="caption">S&amp;T France · 123back Collect</Typography>
        </Container>
      </Box>
    </Box>
  );
}
